// Zod DTOs for `GET /v1/me/search?q=` (P3-07, spec §6.3 "SQLite FTS5 across
// visible sections + resource titles ... intersected with the caller's
// effective-access set (§4)"). Query param only (no request body) — the
// response shape mirrors `apps/api/src/prisma/search.ts`'s
// `SectionSearchHit`/`ResourceSearchHit` raw-SQL row shapes, trimmed to the
// fields a student-facing result actually needs (never `rank`, an internal
// FTS5 ordering signal, not part of the public contract).
import { z } from 'zod';
import type { MeAncestor } from './me';

export const meSearchQuerySchema = z.object({
  /** FTS5 query text, taken as-is from the caller — sanitized/tokenized
   * server-side (`apps/api/src/services/search.ts`'s `buildFtsMatchQuery`)
   * before ever reaching a `MATCH` clause, never string-concatenated into
   * SQL. `min(1)` rejects an empty/whitespace-only query before it reaches
   * the DB layer at all. */
  q: z.string().trim().min(1).max(200),
});
export type MeSearchQuery = z.infer<typeof meSearchQuerySchema>;

/** Friendly-URL ancestry (docs/ADR-friendly-urls.md §5), additive-only —
 * lets a search-result link go straight to the friendly section URL at
 * click-time instead of the legacy id route + client-side `router.replace()`
 * hop. `slug` + `ancestors` (`Section`-typed entries only) are exactly the
 * shape `apps/web/src/data/learn/slugResolve.ts`'s `buildSectionSlugPath`
 * already expects, so the frontend can call it directly with a search hit. */
interface MeSearchAncestryFields {
  slug: string;
  levelId: string;
  subjectSlug: string;
  subjectName: string;
  levelSlug: string;
  levelName: string;
  boardSlug: string;
  boardName: string;
  ancestors: MeAncestor[];
}

export interface MeSearchSectionHit extends MeSearchAncestryFields {
  id: string;
  subjectId: string;
  title: string;
  summary: string | null;
}

export interface MeSearchResourceHit extends MeSearchAncestryFields {
  id: string;
  tabId: string;
  sectionId: string;
  subjectId: string;
  title: string;
}

export interface MeSearchResponse {
  sections: MeSearchSectionHit[];
  resources: MeSearchResourceHit[];
}
